import React from 'react';

/**
 * The 5f "F-point" mark.
 *
 * Geometry is verbatim from docs/revamp-july-2026/handoff/BRAND-SPEC.md
 * "Logo (5f)": a 24-unit grid at stroke 1.7, where a stem and two bars form an
 * F and a ring closes its open counter as the focal point.
 *
 * Everything paints in `currentColor`, so the mark is single-colour safe in
 * --ink-blue, --text, and reversed on --card / --sand-accent, which the spec
 * requires. Override the colour with a text utility on the element or a parent.
 */

type LogoVariant = 'reduction' | 'full';

interface LogoProps {
  /** Rendered size of the mark in pixels. The wordmark scales independently. */
  size?: number;
  /**
   * 'reduction' is the ring and dot only. It is the header and inline lockup,
   * so an "Fo" shape never sits directly beside the word "Focal".
   * 'full' is the complete F-plus-ring mark, for standalone use.
   */
  variant?: LogoVariant;
  /** Render the "Focal Point Solutions" wordmark beside the mark. */
  showText?: boolean;
  /** Font size of the wordmark in pixels. Header is 19, footer is 16. */
  textSize?: number;
  className?: string;
}

function FullMark({ size }: { size: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="3" y="2" width="1.7" height="20" />
      <rect x="3" y="2" width="13" height="1.7" />
      <rect x="3" y="10.2" width="9.4" height="1.7" />
      <circle cx="18" cy="18" r="3.3" fill="none" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function ReductionMark({ size }: { size: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.9" />
      <circle cx="12" cy="12" r="3.4" />
    </svg>
  );
}

export default function Logo({
  size = 24,
  variant = 'reduction',
  showText = false,
  textSize = 19,
  className = '',
}: LogoProps) {
  return (
    <span className={`inline-flex items-center gap-3 text-ink-blue ${className}`.trim()}>
      {variant === 'full' ? <FullMark size={size} /> : <ReductionMark size={size} />}

      {showText ? (
        <span
          className="font-display font-medium tracking-[-0.03em] text-ink leading-none"
          style={{ fontSize: textSize }}
        >
          Focal Point Solutions
        </span>
      ) : (
        <span className="sr-only">Focal Point Solutions</span>
      )}
    </span>
  );
}
